import React, { useState } from 'react';
import { useThemes } from '../../hooks/useThemes';
import MainLayout from "core/components/layout/MainLayout";
import Loading from "core/components/Loading";
import Error from "core/components/Error";
import BreadcrumbWidget from "core/components/shared/BreadcrumbWidget";
import TimeAgoWidget from "core/components/shared/TimeAgoWidget";
import Pagination from 'core/components/shared/Pagination';
import { Button, Modal } from 'react-bootstrap';
import ThemeForm from '../../components/ThemeForm';
import { useMutation, useQueryClient } from 'react-query';
import { ContentHttpService, Theme } from '../../services';
import { Link } from 'react-router-dom';

const ThemePage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTheme, setEditingTheme] = useState<Theme | undefined>(undefined);
  const queryClient = useQueryClient();

  const { themes, loading, error,refetch } = useThemes({ page, pageSize });

  console.log(themes);


  const deleteMutation = useMutation({
    mutationFn: (themeId: number) => ContentHttpService.deleteTheme(themeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['themes'] });
    },
  });

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleEdit = (theme: Theme) => {
    setEditingTheme(theme);
    setShowAddModal(true);
  };

  const handleDelete = async (themeId: number) => {
    if (window.confirm('Are you sure you want to delete this theme?')) {
      await deleteMutation.mutateAsync(themeId);
    }
  };

  const handleCloseModal = () => {
    refetch();
    setShowAddModal(false);
    setEditingTheme(undefined);
  };

  return (
    <MainLayout>
      <div className="container-fluid">
        <BreadcrumbWidget
          mainTitle={`Themes (${themes.length})`}
          breadcrumbs={[
            { title: "Dashboard", url: "/" },
            { title: "Themes" },
          ]}
        />

        <div className="row">
          <div className="col-xl-12">
            <div className="card custom-card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">Themes</h5>
                <Button 
                  variant="primary" 
                  onClick={() => setShowAddModal(true)}
                >
                  <i className="ri-add-line me-1"></i>
                  Add Theme
                </Button>
              </div>

              <div className="card-body">
                {loading && <Loading isLoading={loading} />}
                
                {/* {error && (<Error error={error as string} />)} */}
                
                {!loading && (!themes || themes.length === 0) && (
                  <div className="alert alert-info" role="alert">
                    No themes found. Add a theme to get started.
                  </div>
                )}
                
                {themes && themes.length > 0 && (
                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead className="table-light">
                        <tr>
                          <th>ID</th>
                          <th>Title</th>
                          <th>Description</th>
                          <th>Featured</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {themes.map((theme) => (
                          <tr key={theme.id}>
                            <td>{theme.id}</td>
                            <td>
                              <Link title="View use cases" to={`/usecases/${theme.id}/${theme.title.replace(/\s+/g, '-')}`}>
                                {theme.title}
                              </Link>
                            </td>
                            <td>
                              <Link title="View use cases" to={`/usecases/${theme.id}/${theme.title.replace(/\s+/g, '-')}`}>
                                {theme.description}
                              </Link>
                              </td>
                            <td>
                              {theme.featured ? (
                                <i className="ri-star-fill text-warning"></i>
                              ) : (
                                <i className="ri-star-line text-muted"></i>
                              )}
                            </td>
                            <td>
                              <TimeAgoWidget date={theme.created_at} />
                            </td>
                            <td>
                              <div className="btn-group">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEdit(theme)}
                                >
                                  <i className="ri-edit-line"></i>
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDelete(theme.id)}
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <Pagination
                        currentPage={page}
                        totalItems={themes.length}
                        pageSize={pageSize}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showAddModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingTheme ? 'Edit Theme' : 'Add New Theme'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ThemeForm 
            theme={editingTheme}
            onSuccess={handleCloseModal}
          />
        </Modal.Body>
      </Modal>
    </MainLayout>
  );
};

export default ThemePage; 